import { parseMemberContext } from "./member-context";
import { expectArray, expectBoolean, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
export const INVITE_SCOPE_TYPES = ["student", "family"];
export const RELATION_TYPES = ["parent", "guardian"];
export const GUARDIAN_INVITE_STATUSES = ["active", "consumed", "expired", "revoked"];
export const GUARDIAN_LINK_REQUEST_STATUSES = ["submitted", "approved", "rejected", "cancelled", "completed"];
export const USER_STUDENT_LINK_STATUSES = ["active", "revoked"];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
export function parseInviteScopeType(value, path = "scopeType") {
    return parseStringEnum(value, INVITE_SCOPE_TYPES, path);
}
export function parseRelationType(value, path = "relationType") {
    return parseStringEnum(value, RELATION_TYPES, path);
}
export function parseGuardianInviteStatus(value, path = "inviteStatus") {
    return parseStringEnum(value, GUARDIAN_INVITE_STATUSES, path);
}
export function parseGuardianLinkRequestStatus(value, path = "guardianLinkRequestStatus") {
    return parseStringEnum(value, GUARDIAN_LINK_REQUEST_STATUSES, path);
}
export function parseUserStudentLinkStatus(value, path = "userStudentLinkStatus") {
    return parseStringEnum(value, USER_STUDENT_LINK_STATUSES, path);
}
export function parseGuardianInviteStudent(value, path = "guardianInviteStudent") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        displayName: expectString(record.displayName, `${path}.displayName`)
    };
}
export function parseResolveGuardianInviteRequest(value, path = "resolveGuardianInviteRequest") {
    const record = expectRecord(value, path);
    const inviteToken = expectOptionalString(record.inviteToken, `${path}.inviteToken`);
    const manualCode = expectOptionalString(record.manualCode, `${path}.manualCode`);
    if (!inviteToken && !manualCode) {
        throw new Error(`${path} requires inviteToken or manualCode`);
    }
    return {
        inviteToken,
        manualCode
    };
}
export function parseResolvedGuardianInvite(value, path = "resolvedGuardianInvite") {
    const record = expectRecord(value, path);
    return {
        inviteId: expectString(record.inviteId, `${path}.inviteId`),
        scopeType: parseInviteScopeType(record.scopeType, `${path}.scopeType`),
        relationTypeDefault: parseRelationType(record.relationTypeDefault, `${path}.relationTypeDefault`),
        status: parseGuardianInviteStatus(record.status, `${path}.status`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`),
        maxUseCount: expectNumber(record.maxUseCount, `${path}.maxUseCount`),
        usedCount: expectNumber(record.usedCount, `${path}.usedCount`),
        remainingUseCount: expectNumber(record.remainingUseCount, `${path}.remainingUseCount`),
        familyAccountId: expectOptionalString(record.familyAccountId, `${path}.familyAccountId`),
        students: expectArray(record.students, parseGuardianInviteStudent, `${path}.students`),
        alreadyConsumedByCurrentUser: expectBoolean(record.alreadyConsumedByCurrentUser, `${path}.alreadyConsumedByCurrentUser`)
    };
}
export function parseConsumeGuardianInviteRequest(value, path = "consumeGuardianInviteRequest") {
    const record = expectRecord(value, path);
    return {
        inviteId: expectString(record.inviteId, `${path}.inviteId`)
    };
}
export function parseConsumeGuardianInviteResponse(value, path = "consumeGuardianInviteResponse") {
    const record = expectRecord(value, path);
    return {
        inviteId: expectString(record.inviteId, `${path}.inviteId`),
        alreadyConsumed: expectBoolean(record.alreadyConsumed, `${path}.alreadyConsumed`),
        linkedStudentIds: expectArray(record.linkedStudentIds, expectString, `${path}.linkedStudentIds`),
        createdLinkIds: expectArray(record.createdLinkIds, expectString, `${path}.createdLinkIds`),
        member: parseMemberContext(record.member, `${path}.member`)
    };
}
export function parseCreateGuardianLinkRequest(value, path = "createGuardianLinkRequest") {
    const record = expectRecord(value, path);
    return {
        requestedRelationType: parseRelationType(record.requestedRelationType, `${path}.requestedRelationType`),
        studentName: expectString(record.studentName, `${path}.studentName`),
        birthDate: expectOptionalString(record.birthDate, `${path}.birthDate`),
        supportingInfo: expectOptionalString(record.supportingInfo, `${path}.supportingInfo`)
    };
}
export function parseGuardianLinkRequest(value, path = "guardianLinkRequest") {
    const record = expectRecord(value, path);
    return {
        requestId: expectString(record.requestId, `${path}.requestId`),
        requesterPlatformUserId: expectString(record.requesterPlatformUserId, `${path}.requesterPlatformUserId`),
        requestedRelationType: parseRelationType(record.requestedRelationType, `${path}.requestedRelationType`),
        studentName: expectString(record.studentName, `${path}.studentName`),
        birthDate: expectOptionalString(record.birthDate, `${path}.birthDate`),
        supportingInfo: expectOptionalString(record.supportingInfo, `${path}.supportingInfo`),
        status: parseGuardianLinkRequestStatus(record.status, `${path}.status`),
        resolvedStudentIds: expectArray(record.resolvedStudentIds, expectString, `${path}.resolvedStudentIds`),
        resolvedFamilyAccountId: expectOptionalString(record.resolvedFamilyAccountId, `${path}.resolvedFamilyAccountId`),
        reviewedByPlatformUserId: expectOptionalString(record.reviewedByPlatformUserId, `${path}.reviewedByPlatformUserId`),
        reviewedAt: expectOptionalString(record.reviewedAt, `${path}.reviewedAt`),
        internalNote: expectOptionalString(record.internalNote, `${path}.internalNote`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseGuardianLinkRequestListResponse(value, path = "guardianLinkRequestListResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseGuardianLinkRequest, `${path}.items`)
    };
}
